import { Action, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { TraceabilityStateModel } from './traceability.model';
import { TraceabilityService } from './traceability.service';
import { Header, Pagination } from '../general-store/general.model';
import {
  GetTraceabilityRecentEvents,
  GetTraceabilityStats,
  GetTraceabilityHistory,
  VerifyTraceabilityRecord,
  ResetTraceabilityState,
  ClearTraceabilityHistory,
} from './traceability.actions';

const TRACEABILITY_HEADERS: Header[] = [
  {
    label: 'Timestamp',
    key: 'eventTimestamp',
    isVisibleHeader: true,
    sortable: true,
    filterable: false,
    filterValues: [],
  },
  {
    label: 'Event Type',
    key: 'eventType',
    isVisibleHeader: true,
    sortable: true,
    filterable: true,
    filterValues: ['INTAKE', 'UPDATE_ARCHIVE', 'CURRENT_STATE'],
  },
  {
    label: 'Signer',
    key: 'signerId',
    isVisibleHeader: true,
    sortable: true,
    filterable: false,
    filterValues: [],
  },
  {
    label: 'Hash Snippet',
    key: 'eventHash',
    isVisibleHeader: true,
    sortable: false,
    filterable: false,
    filterValues: [],
  },
  {
    label: 'Status',
    key: 'status',
    isVisibleHeader: true,
    sortable: false,
    filterable: false,
    filterValues: [],
  },
  {
    label: 'Actions',
    key: 'actions',
    isVisibleHeader: true,
    sortable: false,
    filterable: false,
    filterValues: [],
  },
];

@State<TraceabilityStateModel>({
  name: 'Traceability',
  defaults: {
    isLoading: false,
    recentEvents: [],
    stats: null,
    history: [],
    verificationResult: null,
    error: null,
    headers: TRACEABILITY_HEADERS,
    pagination: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0,
    },
  },
})
@Injectable({
  providedIn: 'root',
})
export class TraceabilityState {
  private readonly traceabilityService = inject(TraceabilityService);

  @Action(GetTraceabilityRecentEvents)
  getRecentEvents(
    { patchState, getState }: StateContext<TraceabilityStateModel>,
    { page, limit }: GetTraceabilityRecentEvents,
  ) {
    patchState({ isLoading: true });
    const currentPage = page ?? 1;
    const itemsPerPage = limit ?? getState().pagination?.itemsPerPage ?? 10;
    return this.traceabilityService.getRecentEvents(currentPage, itemsPerPage).pipe(
      tap((res) => {
        if (res.status === 'success' && res.payload) {
          const events = (res.payload.events || []).map((e: any) => ({
            ...e,
            eventTimestamp: e.createdAt,
            // Avoid full-hash + textTransform noise; empty when unsigned historical rows
            eventHash: e.eventHash
              ? `${String(e.eventHash).slice(0, 12)}…`
              : '—',
            status:
              e.integrityStatus || (e.isValid ? 'SECURED' : e.signature ? 'TAMPERED' : 'PENDING'),
            // HISTORY is shown in the row menu; VIEW is filtered from inline/menu by data-table
            actions: ['HISTORY'],
          }));
          const pagination = res.pagination || getState().pagination;
          patchState({
            recentEvents: events,
            pagination: {
              ...pagination,
              totalItems: res.payload.totalCount ?? pagination?.totalItems ?? events.length,
            },
            isLoading: false,
          });
        }
      }),
      catchError((err) => {
        patchState({ error: err.message, isLoading: false });
        return of(err);
      }),
    );
  }

  @Action(GetTraceabilityStats)
  getStats({ patchState }: StateContext<TraceabilityStateModel>) {
    patchState({ isLoading: true });
    return this.traceabilityService.getStats().pipe(
      tap((res) => {
        if (res.status === 'success' && res.payload) {
          patchState({
            stats: res.payload,
            isLoading: false,
          });
        }
      }),
      catchError((err) => {
        patchState({ error: err.message, isLoading: false });
        return of(err);
      }),
    );
  }

  @Action(GetTraceabilityHistory)
  getHistory(
    { patchState }: StateContext<TraceabilityStateModel>,
    { qrProductDataId }: GetTraceabilityHistory,
  ) {
    patchState({ isLoading: true });
    return this.traceabilityService.getHistory(qrProductDataId).pipe(
      tap((res) => {
        if (res.status === 'success' && res.payload) {
          patchState({
            history: res.payload.versions || [],
            isLoading: false,
          });
        }
      }),
      catchError((err) => {
        patchState({ error: err.message, isLoading: false });
        return of(err);
      }),
    );
  }

  @Action(VerifyTraceabilityRecord)
  verifyRecord(
    { patchState, getState }: StateContext<TraceabilityStateModel>,
    { qrProductDataId }: VerifyTraceabilityRecord,
  ) {
    patchState({ isLoading: true });
    return this.traceabilityService.verifyRecord(qrProductDataId).pipe(
      tap((res) => {
        if (res.status === 'success' && res.payload) {
          patchState({
            verificationResult: res.payload,
            history: res.payload.history || getState().history, // Update history if provided (Refresh timeline)
            isLoading: false,
          });
        } else {
          // Fallback if payload isn't nested but is the response itself
          patchState({
            verificationResult: res.payload || res,
            isLoading: false,
          });
        }
      }),
      catchError((err) => {
        patchState({ error: err.message, isLoading: false });
        return of(err);
      }),
    );
  }

  @Action(ResetTraceabilityState)
  resetState({ patchState }: StateContext<TraceabilityStateModel>) {
    patchState({
      recentEvents: [],
      stats: null,
      history: [],
      verificationResult: null,
      error: null,
    });
  }

  @Action(ClearTraceabilityHistory)
  clearHistory({ patchState }: StateContext<TraceabilityStateModel>) {
    patchState({
      history: [],
      verificationResult: null,
      error: null,
    });
  }
}
